import { localStorageUtil } from 'utils/storage';
import { STORAGE_KEYS } from 'constants/storageKeys';

/**
 * Single owner of session persistence. The axios layer, auth service, and redux
 * all read/write through here so there is one storage contract.
 *
 * Persists tokens AND the user profile (its `permissions` matrix included).
 * The backend exposes no "current user" endpoint, so the profile captured at
 * login is restored from storage on reload (the access token is still
 * validated/refreshed by the axios interceptor on the next protected
 * request).
 */
export const tokenService = {
  getAccessToken: () => localStorageUtil.get(STORAGE_KEYS.ACCESS_TOKEN),
  getRefreshToken: () => localStorageUtil.get(STORAGE_KEYS.REFRESH_TOKEN),

  setTokens: ({ accessToken, refreshToken } = {}) => {
    if (accessToken) localStorageUtil.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    if (refreshToken) localStorageUtil.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  },

  getUser: () => localStorageUtil.get(STORAGE_KEYS.USER),
  setUser: (user) => {
    if (user) localStorageUtil.set(STORAGE_KEYS.USER, user);
  },

  /** Persist the full session captured at login. */
  setSession: ({ accessToken, refreshToken, user } = {}) => {
    tokenService.setTokens({ accessToken, refreshToken });
    if (user) tokenService.setUser(user);
  },

  /** Clear everything on logout / session expiry. */
  clearTokens: () => {
    localStorageUtil.remove(STORAGE_KEYS.ACCESS_TOKEN);
    localStorageUtil.remove(STORAGE_KEYS.REFRESH_TOKEN);
    localStorageUtil.remove(STORAGE_KEYS.USER);
    // Clears any sidebar entry persisted by older sessions before this change.
    localStorageUtil.remove(STORAGE_KEYS.SIDEBAR);
  },

  hasSession: () => Boolean(localStorageUtil.get(STORAGE_KEYS.ACCESS_TOKEN)),
};

export default tokenService;
